import React from "react";
import { Button, Stack, Typography } from "@mui/material";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import { formatFileSize } from "../../utils";
import DocumentThumbnail from "./DocumentThumbnail";
import s from "../MerchantDocumentUpload.module.scss";

const UploadDialog = ({
  open,
  pendingUpload,
  pendingUploadValidation,
  pendingCategoryLabel,
  isPendingUploadUploading,
  pendingUploadPreviewDocuments,
  onClose,
  onConfirm,
  onRemovePendingDocument,
  t,
  localePrefix,
  maxFileSizeMb,
  maxFilesPerCategory,
  invalidFileNameChars,
}) => (
  <SimpleDialog
    open={open}
    dialogOncloseFn={onClose}
    title={t(`${localePrefix}dialog.uploadTitle`, {
      category: pendingCategoryLabel,
    })}
    content={
      pendingUpload ? (
        <Stack gap={1.5}>
          <Typography variant="body2">
            {t(`${localePrefix}dialog.uploadContent`, {
              count: pendingUpload.documents.length,
            })}
          </Typography>
          {pendingUploadValidation?.exceedsLimit && (
            <Typography variant="caption" color="error">
              {t(`${localePrefix}dialog.limitWarning`, {
                max: maxFilesPerCategory,
                existing: pendingUploadValidation.existingCount,
                selected: pendingUploadValidation.selectedCount,
                total: pendingUploadValidation.totalAfterUpload,
                removeCount: pendingUploadValidation.overLimitBy,
                remaining: pendingUploadValidation.remainingSlots,
              })}
            </Typography>
          )}
          <Stack gap={0.75}>
            {pendingUploadPreviewDocuments.map(
              ({ document, duplicate, oversize, invalidName, previewUrl }, index) => (
                <Stack
                  key={`${document.name || "document"}-${document.lastModified ?? index}`}
                  gap={0.25}
                >
                  <Stack direction="row" gap={1} alignItems="center" className={s.previewRow}>
                    <DocumentThumbnail document={document} previewUrl={previewUrl} renderPdfAsObject={true} />
                    <Typography variant="body2">• {document.name}</Typography>
                  </Stack>
                  {(duplicate || oversize || invalidName) && (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      gap={{ xs: 0.5, sm: 1 }}
                      alignItems="flex-start"
                    >
                      <Stack gap={0.25}>
                        {duplicate && (
                          <Typography variant="caption" color="error" component="span">
                            {t(`${localePrefix}dialog.warnings.duplicate`)}
                          </Typography>
                        )}
                        {oversize && (
                          <Typography variant="caption" color="error" component="span">
                            {t(`${localePrefix}dialog.warnings.size`, {
                              size: maxFileSizeMb,
                              currentSize: formatFileSize(document.size),
                            })}
                          </Typography>
                        )}
                        {invalidName && (
                          <Typography variant="caption" color="error" component="span">
                            {t(`${localePrefix}dialog.warnings.invalidName`, {
                              chars: invalidFileNameChars,
                            })}
                          </Typography>
                        )}
                      </Stack>
                      <Button
                        size="small"
                        color="error"
                        variant="text"
                        onClick={() => onRemovePendingDocument(index)}
                        sx={{ minWidth: "auto", padding: 0 }}
                      >
                        {t(`${localePrefix}dialog.removeInvalid`)}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              ),
            )}
          </Stack>
        </Stack>
      ) : (
        ""
      )
    }
    positiveAction={t(`${localePrefix}dialog.confirm`)}
    negativeAction={t(`${localePrefix}dialog.cancel`)}
    positiveActionLoading={!!isPendingUploadUploading}
    negativeActionDisabled={!!isPendingUploadUploading}
    positiveActionDisabled={
      !!pendingUploadValidation?.exceedsLimit ||
      !!pendingUploadValidation?.hasInvalidName ||
      !!isPendingUploadUploading
    }
    confirmFn={onConfirm}
  />
);

export default UploadDialog;
